/**
 * useMembers Hook
 *
 * Manages team members and invitations for an organization with real-time updates.
 *
 * @module hooks/useMembers
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  orderBy,
  where,
  onSnapshot,
  type QuerySnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { OrganizationMember, Invitation, OrgRole } from '@/lib/firebase-types';

// =============================================================================
// Types
// =============================================================================

interface UseMembersResult {
  /** List of organization members */
  members: OrganizationMember[];
  /** List of pending invitations */
  invitations: Invitation[];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;

  // Actions
  /** Invite a new member */
  inviteMember: (email: string, role: OrgRole) => Promise<void>;
  /** Remove a member */
  removeMember: (userId: string) => Promise<void>;
  /** Update a member's role */
  updateMemberRole: (userId: string, role: OrgRole) => Promise<void>;
  /** Revoke a pending invitation */
  revokeInvitation: (invitationId: string) => Promise<void>;
  /** Leave the organization */
  leaveOrganization: () => Promise<void>;
  /** Transfer ownership to another member */
  transferOwnership: (newOwnerId: string) => Promise<void>;
  /** Refresh the data */
  refresh: () => void;

  // Computed
  /** Current user can manage members */
  canManageMembers: boolean;
  /** Current user can invite members */
  canInviteMembers: boolean;
  /** Current user can access billing */
  canAccessBilling: boolean;
  /** Current user can update org settings */
  canUpdateOrgSettings: boolean;
  /** Current user can transfer ownership */
  canTransferOwnership: boolean;
  /** Current user's role in this org */
  currentUserRole: OrgRole | null;
  /** Roles the current user can invite as */
  invitableRoles: OrgRole[];
  /** Number of members */
  memberCount: number;
  /** Number of pending invitations */
  pendingInviteCount: number;
}

// Response types for Cloud Functions
interface SendInvitationResponse {
  invitationId: string;
  expiresAt: string;
}

interface AcceptInvitationResponse {
  organizationId: string;
  organizationName: string;
}

interface SimpleResponse {
  success: boolean;
}

// =============================================================================
// Hook
// =============================================================================

export function useMembers(orgId: string | undefined): UseMembersResult {
  const { user } = useAuth();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ---------------------------------------------------------------------------
  // Real-time Listeners
  // ---------------------------------------------------------------------------

  // Members listener
  useEffect(() => {
    if (!orgId) {
      setMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const membersRef = collection(db, 'organizations', orgId, 'members');
    const membersQuery = query(membersRef, orderBy('joinedAt', 'asc'));

    const unsubscribe = onSnapshot(
      membersQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const membersData: OrganizationMember[] = snapshot.docs.map((doc) => ({
          ...doc.data(),
          userId: doc.id,
        })) as OrganizationMember[];

        setMembers(membersData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching members:', err);
        setError('Failed to load team members.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId, refreshTrigger]);

  // Invitations listener
  useEffect(() => {
    if (!orgId) {
      setInvitations([]);
      return;
    }

    const invitesRef = collection(db, 'organizations', orgId, 'invitations');
    const invitesQuery = query(
      invitesRef,
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      invitesQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const invitesData: Invitation[] = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as Invitation[];

        setInvitations(invitesData);
      },
      (err) => {
        console.error('Error fetching invitations:', err);
        // Don't set error for invitations - members are more important
      }
    );

    return () => unsubscribe();
  }, [orgId, refreshTrigger]);

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const currentUserRole = useMemo((): OrgRole | null => {
    if (!user || !orgId) return null;
    const membership = user.orgMemberships?.find((m) => m.orgId === orgId);
    return membership?.role || null;
  }, [user, orgId]);

  // Permission computations based on 6-role system
  const canManageMembers = ['owner', 'org_admin', 'billing_admin', 'admin', 'manager'].includes(currentUserRole || '');
  const canInviteMembers = ['owner', 'org_admin', 'billing_admin', 'admin', 'manager'].includes(currentUserRole || '');
  const canAccessBilling = ['owner', 'org_admin', 'billing_admin'].includes(currentUserRole || '');
  const canUpdateOrgSettings = ['owner', 'org_admin'].includes(currentUserRole || '');
  const canTransferOwnership = currentUserRole === 'owner';
  const memberCount = members.length;
  const pendingInviteCount = invitations.length;

  // Compute invitable roles based on current user's role
  const invitableRoles = useMemo((): OrgRole[] => {
    switch (currentUserRole) {
      case 'owner':
      case 'org_admin':
        return ['org_admin', 'billing_admin', 'admin', 'manager', 'member'];
      case 'billing_admin':
      case 'admin':
        // Billing Admin and Admin can only invite admin, manager, member (NOT billing_admin)
        return ['admin', 'manager', 'member'];
      case 'manager':
        return ['member'];
      default:
        return [];
    }
  }, [currentUserRole]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const inviteMember = useCallback(
    async (email: string, role: OrgRole): Promise<void> => {
      if (!orgId) {
        throw new Error('Organization not found.');
      }

      const sendInvitation = httpsCallable<
        { email: string; role: OrgRole; organizationId: string },
        SendInvitationResponse
      >(functions, 'sendInvitation');

      await sendInvitation({ email, role, organizationId: orgId });
    },
    [orgId]
  );

  const removeMember = useCallback(
    async (userId: string): Promise<void> => {
      if (!orgId) {
        throw new Error('Organization not found.');
      }

      const removeMemberFn = httpsCallable<
        { userId: string; organizationId: string },
        SimpleResponse
      >(functions, 'removeMember');

      const result = await removeMemberFn({ userId, organizationId: orgId });

      if (!result.data.success) {
        throw new Error('Failed to remove member.');
      }
    },
    [orgId]
  );

  const updateMemberRole = useCallback(
    async (userId: string, newRole: OrgRole): Promise<void> => {
      if (!orgId) {
        throw new Error('Organization not found.');
      }

      const updateMemberRoleFn = httpsCallable<
        { userId: string; organizationId: string; newRole: OrgRole },
        SimpleResponse
      >(functions, 'updateMemberRole');

      const result = await updateMemberRoleFn({
        userId,
        organizationId: orgId,
        newRole,
      });

      if (!result.data.success) {
        throw new Error('Failed to update member role.');
      }
    },
    [orgId]
  );

  const revokeInvitation = useCallback(
    async (invitationId: string): Promise<void> => {
      if (!orgId) {
        throw new Error('Organization not found.');
      }

      const revokeInvitationFn = httpsCallable<
        { invitationId: string; organizationId: string },
        SimpleResponse
      >(functions, 'revokeInvitation');

      const result = await revokeInvitationFn({
        invitationId,
        organizationId: orgId,
      });

      if (!result.data.success) {
        throw new Error('Failed to revoke invitation.');
      }
    },
    [orgId]
  );

  const leaveOrganization = useCallback(async (): Promise<void> => {
    if (!orgId) {
      throw new Error('Organization not found.');
    }

    const leaveOrgFn = httpsCallable<
      { organizationId: string },
      SimpleResponse
    >(functions, 'leaveOrganization');

    const result = await leaveOrgFn({ organizationId: orgId });

    if (!result.data.success) {
      throw new Error('Failed to leave organization.');
    }
  }, [orgId]);

  const transferOwnership = useCallback(
    async (newOwnerId: string): Promise<void> => {
      if (!orgId) {
        throw new Error('Organization not found.');
      }

      const transferOwnershipFn = httpsCallable<
        { newOwnerId: string; organizationId: string },
        SimpleResponse
      >(functions, 'transferOwnership');

      const result = await transferOwnershipFn({
        newOwnerId,
        organizationId: orgId,
      });

      if (!result.data.success) {
        throw new Error('Failed to transfer ownership.');
      }
    },
    [orgId]
  );

  const refresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    members,
    invitations,
    loading,
    error,
    inviteMember,
    removeMember,
    updateMemberRole,
    revokeInvitation,
    leaveOrganization,
    transferOwnership,
    refresh,
    canManageMembers,
    canInviteMembers,
    canAccessBilling,
    canUpdateOrgSettings,
    canTransferOwnership,
    currentUserRole,
    invitableRoles,
    memberCount,
    pendingInviteCount,
  };
}

// =============================================================================
// Accept Invitation Hook
// =============================================================================

interface UseAcceptInvitationResult {
  /** Accept an invitation by token */
  acceptInvitation: (token: string) => Promise<AcceptInvitationResponse>;
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
}

export function useAcceptInvitation(): UseAcceptInvitationResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptInvitation = useCallback(
    async (token: string): Promise<AcceptInvitationResponse> => {
      setLoading(true);
      setError(null);

      try {
        const acceptInvitationFn = httpsCallable<
          { token: string },
          AcceptInvitationResponse
        >(functions, 'acceptInvitation');

        const result = await acceptInvitationFn({ token });
        return result.data;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to accept invitation.';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    acceptInvitation,
    loading,
    error,
  };
}
